import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { ActionStatus as Action_Status_Enum } from '@risksmart-app/domain/src/types/consts/action-status';
import type { InsertChildActionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertChildActionDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface InsertChildActionInput {
  Title: string;
  DateDue: string;
  DateRaised: string;
  Status: Action_Status_Enum;
  ParentId?: string | null;
  Priority?: number | null;
  Description?: string | null;
  ClosedDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
}

export const useInsertChildAction = createMutationHook({
  name: 'insertChildAction',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.action.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (input: InsertChildActionInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.action.register.queryKey(),
    trpc.frontend.action.actionById.queryKey(),
    // COI-889: the internal-audit register carries open/closed-action count
    // columns, and the consumer evicts `internal_audit_entity` on Apollo.
    trpc.frontend.internalAuditEntity.pathKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
    // COI-577: drives the obligation-changes register's `ActionsLabelled`
    // column. Previously evicted at the detail-page call-site, which was a
    // no-op on the tRPC leg.
    trpc.frontend.obligationChange.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertChildActionMutation => ({
    insertChildAction: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertChildActionDocument,
  graphqlVariables: (input: InsertChildActionInput) => ({ ...input }),
  // COI-577: `internal_audit_entity` (open/closed-action counts, COI-889) and
  // `obligation_change` (`ActionsLabelled`) moved here from call-sites, so
  // every consumer gets them on both legs rather than only where a call-site
  // remembered to.
  evictFields: [
    'action',
    'action_aggregate',
    'internal_audit_entity',
    'obligation_change',
  ],
  refetchQueries: [namedOperations.Query.getActionById],
});
